import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { FileBadge, Pencil } from "lucide-react";
import { CertificationForm } from "./CertificationForm";
import { format, isValid, toDate } from "date-fns";

const MISSING_EXPIRATION_LABEL = "not provided";

/**
 * Certification values are stored as free-form JSON, so a record can be missing
 * its expiration date or hold one that does not parse. date-fns throws a
 * RangeError for those, which would crash the whole profile page.
 */
function formatExpirationMonth(expirationDate: string | Date | undefined) {
  if (!expirationDate) {
    return MISSING_EXPIRATION_LABEL;
  }
  const date = toDate(expirationDate);
  return isValid(date) ? format(date, "MMM yyyy") : MISSING_EXPIRATION_LABEL;
}

type CertificationEntry = {
  value: unknown;
};
type DeaCertification = {
  number: string;
  expiration_date: string | Date;
};
type CdsCertification = {
  number: string;
  expiration_date: string | Date;
};
type BlsAclsCertification = {
  ecard_code: string;
  expiration_date: string | Date;
};
type Certifications = {
  dea: CertificationEntry[];
  cds: CertificationEntry[];
  bls: CertificationEntry[];
  acls: CertificationEntry[];
};

export function CertificationsCard({
  editable,
  isEditing,
  onEditChange,
  certifications,
  isProviderHidden = false,
  userId,
}: {
  editable: boolean;
  isEditing: boolean;
  onEditChange: (editing: boolean) => void;
  certifications: Certifications;
  userId: string;
  isProviderHidden?: boolean;
}) {
  return (
    <Card
      className={cn("mt-4", {
        "shadow-none border-none": isEditing,
      })}
    >
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Other Certifications</h3>
          {isEditing || !editable ? null : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditChange(true)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isEditing ? (
          <CertificationForm
            certData={certifications}
            userId={userId}
            onSave={() => onEditChange(false)}
          />
        ) : (
          <>
            {certifications.dea.map((dea, index) => {
              const value = dea.value as DeaCertification;
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileBadge className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">DEA Certification</div>
                      <div
                        className={`text-sm ${isProviderHidden ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {isProviderHidden ? "# Hidden" : `#${value.number}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        Exp. {formatExpirationMonth(value.expiration_date)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {certifications.cds.map((cds, index) => {
              const value = cds.value as CdsCertification;
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileBadge className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">CDS Certification</div>
                      <div
                        className={`text-sm ${isProviderHidden ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {isProviderHidden ? "# Hidden" : `#${value.number}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        Exp. {formatExpirationMonth(value.expiration_date)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {certifications.bls.map((bls, index) => {
              const value = bls.value as BlsAclsCertification;
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileBadge className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">BLS Certification</div>
                      <div
                        className={`text-sm ${isProviderHidden ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {isProviderHidden ? "# Hidden" : `#${value.ecard_code}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        Exp. {formatExpirationMonth(value.expiration_date)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {certifications.acls.map((acls, index) => {
              const value = acls.value as BlsAclsCertification;
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileBadge className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">ACLS Certification</div>
                      <div
                        className={`text-sm ${isProviderHidden ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {isProviderHidden ? "# Hidden" : `#${value.ecard_code}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        Exp. {formatExpirationMonth(value.expiration_date)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {certifications.dea.length === 0 &&
              certifications.cds.length === 0 &&
              certifications.bls.length === 0 &&
              certifications.acls.length === 0 && (
                <div className="text-center text-gray-500">
                  No certifications added yet.
                </div>
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
